import * as React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as z from 'zod';

import imagePlaceholder from 'src/assets/images/placeholder.webp';
import { useChangeCardIdentifier } from 'src/features/cards/api/useChangeCardIdentifier';
import { cardMessages } from 'src/features/cards/utils/messages';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { useParking } from 'src/providers/ParkingProvider.tsx';
import SquareIcon from 'src/registry/components/blocks/SquareIcon.tsx';
import { toast } from 'src/registry/hooks/UseToast.ts';
import { Button } from 'src/registry/ui/Button.tsx';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogDescription,
} from 'src/registry/ui/Dialog.tsx';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Image } from 'src/registry/ui/Image';
import { Input } from 'src/registry/ui/Input';
import { generalErrorMessages, generalMessages, validationMessagesMap } from 'src/utils/messages.ts';

import type { InParkingCardDetail } from 'src/features/cards/types';

interface EditCardIdentifierModalFormProps extends React.HTMLAttributes<HTMLDivElement> {
    inParkingCardDetail: InParkingCardDetail;
    refetch: () => void;
}

export type EditCardIdentifierFormSchema = z.infer<typeof editCardIdentifierFormSchema>;

const editCardIdentifierFormSchema = z.object({
    new_indentificator: z.string().min(1, { message: validationMessagesMap.validationErrorRequired }).max(13),
    recognized_identifier: z.string().optional(),
});

const EditCardIdentifierModalForm = ({ inParkingCardDetail, refetch, children }: EditCardIdentifierModalFormProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    if (!inParkingCardDetail) return null;

    const form = useForm<EditCardIdentifierFormSchema>({
        resolver: zodResolver(editCardIdentifierFormSchema),
        mode: 'onChange',
        defaultValues: {},
    });

    const defaultValues = {
        new_indentificator: inParkingCardDetail.card_number,
        recognized_identifier: inParkingCardDetail.card_number,
    };

    React.useEffect(() => {
        if (inParkingCardDetail) {
            form.reset(defaultValues);
        }
    }, [inParkingCardDetail]);

    const { mutate: editIdentifier } = useChangeCardIdentifier({
        onSuccess: () => {
            void refetch();
            setIsModalOpen(false);
            form.reset(defaultValues);
            toast({
                title: formatMessage(cardMessages.editPlateNumberSuccess),
                description: formatMessage(cardMessages.editPlateNumberSuccessDetails),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = (values: EditCardIdentifierFormSchema) => {
        const submitData = {
            parkingId: parkingId,
            cardId: inParkingCardDetail?.in_parking_card_id,
            ...values,
        };
        editIdentifier(submitData);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset(defaultValues);
        }
        setIsModalOpen(isOpen);
    };

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.EDIT} />
                        <div>
                            <DialogTitle>{formatMessage(cardMessages.editPlateNumber)}</DialogTitle>
                            <DialogDescription>{formatMessage(cardMessages.editPlateNumberSubtitle)}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <Image
                        image={`/parkings/images/${parkingId}/${inParkingCardDetail.photo_path}`}
                        placeholder={imagePlaceholder}
                        alt={inParkingCardDetail?.card_number}
                        width={400}
                        height={200}
                    />
                    <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
                        <FormField
                            control={form.control}
                            name="recognized_identifier"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog">
                                        {formatMessage(cardMessages.recognizedPlate)}
                                    </FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            <Input type="text" disabled {...field} />
                                        </FormControl>
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="new_indentificator"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog" required>
                                        {formatMessage(cardMessages.modifiedPlate)}
                                    </FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            <Input
                                                type="text"
                                                placeholder="AB1234CE"
                                                className="uppercase"
                                                autoCapitalize="characters"
                                                maxLength={13}
                                                {...field}
                                                onChange={(e) => {
                                                    const alphanumericOnly = e.target.value.replace(
                                                        /[^a-zA-Z0-9]/g,
                                                        '',
                                                    );
                                                    field.onChange(alphanumericOnly.toUpperCase());
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                    </Form>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1" onClick={() => form.reset()}>
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                        disabled={!form.formState.isDirty || !form.formState.isValid}
                    >
                        {formatMessage(generalMessages.saveChanges)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default EditCardIdentifierModalForm;
